import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CheckCircle2, Loader2, Mail, X } from "lucide-react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import api, { isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import { validateEmail } from "lib/validation";
import swtAlert from "lib/sweetAlert";

export default function ForgotPasswordResetPanel({ defaultEmail, onHide }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [serverMessage, setServerMessage] = useState("");

  useEffect(() => {
    if (defaultEmail) setEmail(defaultEmail);
  }, [defaultEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");

    setIsLoading(true);
    try {
      const resp = await api.post(
        API_ENDPOINTS.forgotPassword,
        { email: email.trim() },
        { auth: false }
      );

      if (!isSuccessResponse(resp)) {
        const msg = resp?.message || "Couldn't send reset link";
        setError(msg);
        await swtAlert.error("Reset link failed", msg);
        return;
      }

      setSentTo(email.trim());
      setServerMessage(
        resp?.message ||
          "If that email is registered, a password reset link has been sent."
      );
      await swtAlert.success(
        "Reset link sent",
        resp?.message || "Reset link sent"
      );
    } catch (err) {
      const msg = err?.message || "Couldn't send reset link";
      setError(msg);
      await swtAlert.error("Reset link failed", msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="rounded-md border border-border bg-card/80 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Reset via email</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            We&apos;ll send a reset link to your account email. Use it to set a new
            password if you don&apos;t remember your current one.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="shrink-0 text-muted-foreground hover:text-foreground"
          onClick={onHide}
          aria-label="Hide forgot password"
        >
          <X className="h-4 w-4" />
          Hide
        </Button>
      </div>

      {sentTo ? (
        <div className="mt-4 space-y-3 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-200">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
            <div>
              <p className="font-medium">Check your email</p>
              <p className="mt-1">{serverMessage}</p>
              <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-300">
                We sent the link to <span className="font-medium">{sentTo}</span>.
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setSentTo("");
              setServerMessage("");
            }}
          >
            Send again
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          <div className="space-y-2">
            <Label htmlFor="forgot-password-email">Email</Label>
            <Input
              id="forgot-password-email"
              name="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError("");
              }}
              aria-invalid={!!error}
              disabled={isLoading}
              autoComplete="email"
            />
            {error ? (
              <p className="text-xs text-destructive">{error}</p>
            ) : null}
          </div>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending link…
              </>
            ) : (
              <>
                <Mail className="mr-2 h-4 w-4" />
                Send reset link
              </>
            )}
          </Button>
        </form>
      )}
    </div>
  );
}

ForgotPasswordResetPanel.propTypes = {
  defaultEmail: PropTypes.string,
  onHide: PropTypes.func.isRequired,
};
